import { useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SELECTABLE_BUILT_IN_SOURCES, leadSourceLabel } from "@/lib/lead-sources";
import { useCreateLeadSource, useLeadSources } from "../api";

const ADD_SENTINEL = "__add_source__";

interface Props {
  value: string;
  onChange: (value: string) => void;
  triggerClassName?: string;
}

/**
 * Lead-source picker shared by contact/company forms. Lists the built-in
 * sources plus the org's custom sources (relay.lead_sources), and offers an
 * inline "Add custom source…" that creates a reusable source and selects it.
 */
export function SourceSelect({ value, onChange, triggerClassName }: Props) {
  const { data: custom = [] } = useLeadSources();
  const create = useCreateLeadSource();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const options = useMemo(() => {
    const customNames = custom.filter((s) => !s.archived).map((s) => s.name);
    const all = [...(SELECTABLE_BUILT_IN_SOURCES as readonly string[]), ...customNames];
    // Keep the current value selectable even if it's a system/legacy source
    // (import/api/booking/document) or an archived custom one.
    if (value && !all.includes(value)) all.push(value);
    return Array.from(new Set(all));
  }, [custom, value]);

  async function saveDraft() {
    const name = draft.trim();
    if (!name) return;
    const src = await create.mutateAsync(name);
    onChange(src.name);
    setDraft("");
    setAdding(false);
  }

  if (adding) {
    return (
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void saveDraft();
            }
            if (e.key === "Escape") {
              setAdding(false);
              setDraft("");
            }
          }}
          placeholder="New source name…"
          disabled={create.isPending}
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-9 w-9 shrink-0"
          disabled={!draft.trim() || create.isPending}
          onClick={() => void saveDraft()}
          aria-label="Save source"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-9 w-9 shrink-0"
          onClick={() => {
            setAdding(false);
            setDraft("");
          }}
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === ADD_SENTINEL) setAdding(true);
        else onChange(v);
      }}
    >
      <SelectTrigger className={triggerClassName}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((s) => (
          <SelectItem key={s} value={s}>
            {leadSourceLabel(s)}
          </SelectItem>
        ))}
        <SelectItem value={ADD_SENTINEL} className="text-muted-foreground">
          <span className="flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add custom source…
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
